import { useCallback, useRef, useState } from "react";
import { reprocessOrdersMock } from "../../mocks/ordersMock";
import type {
  OrderReprocessItemResult,
  OrderReprocessProgress,
} from "../../types/orders";
import {
  ORDER_REPROCESS_ERROR_MESSAGE,
  ORDER_REPROCESS_MAX_BATCH,
  ORDER_REPROCESS_METHOD_NOT_ALLOWED_MESSAGE,
  ORDER_REPROCESS_SUCCESS_MESSAGE,
  ORDER_REPROCESS_MAX_TIMEOUT_MS,
  ORDER_REPROCESS_TIMEOUT_MS,
  ORDER_REPROCESS_UNAVAILABLE_MESSAGE,
  ORDER_REPROCESS_URL,
  parseReprocessResults,
  resolveOrderLoadError,
  translateOrderMessage,
} from "../../types/orders";

interface UseReprocessOrdersOptions {
  onComplete?: (progress: OrderReprocessProgress) => void;
}

interface UseReprocessOrdersReturn {
  reprocessLoading: boolean;
  progressOpen: boolean;
  progress: OrderReprocessProgress | null;
  error: string | null;
  startReprocess: (orderIds: string[]) => Promise<void>;
  closeProgress: () => void;
}

const shouldUseMockOrders = (): boolean =>
  process.env.REACT_APP_USE_ORDERS_MOCK === "true";

const authHeaders = (token: string): HeadersInit => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${token}`,
});

const getAccessToken = (): string => {
  const tokenStorage = localStorage.getItem("token");
  if (!tokenStorage) {
    throw new Error(
      "Token de acceso no encontrado. Inicia sesión primero."
    );
  }
  try {
    const parsed: unknown = JSON.parse(tokenStorage);
    if (typeof parsed === "string" && parsed.trim()) {
      return parsed.trim();
    }
  } catch {
    // Fallback if token was stored without JSON quotes.
  }
  const stripped = tokenStorage.replace(/^["']|["']$/g, "").trim();
  if (!stripped) {
    throw new Error(
      "Token de acceso no encontrado. Inicia sesión primero."
    );
  }
  return stripped;
};

const buildProgress = (
  jobId: string,
  orderIds: string[],
  results: OrderReprocessItemResult[],
  currentOrderVTEXId: string | null,
  status: OrderReprocessProgress["status"],
  message: string
): OrderReprocessProgress => {
  const total = orderIds.length;
  const processed = results.length;
  const pending = Math.max(total - processed, 0);
  const successCount = results.filter((r) => r.success && !r.skipped).length;
  const noveltyCount = results.filter((r) => !r.success || r.skipped).length;
  const percent =
    total === 0 ? 100 : Math.round((processed / total) * 100);

  return {
    jobId,
    total,
    processed,
    pending,
    successCount,
    noveltyCount,
    percent,
    currentOrderVTEXId,
    status,
    message,
    results,
  };
};

/**
 * POST /order/reprocess with up to 20 ids in one request (§3.3).
 * Timeout scales with batch size (backend runs initProcess sequentially).
 */
const reprocessBatch = async (
  orderIds: string[]
): Promise<OrderReprocessItemResult[]> => {
  if (shouldUseMockOrders()) {
    return reprocessOrdersMock(orderIds);
  }

  const token = getAccessToken();
  const controller = new AbortController();
  const timeoutMs = Math.min(
    ORDER_REPROCESS_TIMEOUT_MS * orderIds.length,
    ORDER_REPROCESS_MAX_TIMEOUT_MS
  );
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(ORDER_REPROCESS_URL, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ orderIds }),
      signal: controller.signal,
    });

    if (response.status === 404) {
      return orderIds.map((orderId) => ({
        orderId,
        success: false,
        skipped: false,
        message: ORDER_REPROCESS_UNAVAILABLE_MESSAGE,
        statusBefore: null,
        statusAfter: null,
        processResult: null,
        errorDetail: null,
      }));
    }

    if (response.status === 405) {
      return orderIds.map((orderId) => ({
        orderId,
        success: false,
        skipped: false,
        message: ORDER_REPROCESS_METHOD_NOT_ALLOWED_MESSAGE,
        statusBefore: null,
        statusAfter: null,
        processResult: null,
        errorDetail: "HTTP 405 Method Not Allowed",
      }));
    }

    if (!response.ok) {
      const msg = await resolveOrderLoadError(response);
      return orderIds.map((orderId) => ({
        orderId,
        success: false,
        skipped: false,
        message: msg,
        statusBefore: null,
        statusAfter: null,
        processResult: null,
        errorDetail: msg,
      }));
    }

    const payload = await response.json();
    return parseReprocessResults(payload, orderIds);
  } catch (err) {
    const raw =
      err instanceof Error ? err.message : ORDER_REPROCESS_ERROR_MESSAGE;
    const message = translateOrderMessage(raw, ORDER_REPROCESS_ERROR_MESSAGE);
    return orderIds.map((orderId) => ({
      orderId,
      success: false,
      skipped: false,
      message,
      statusBefore: null,
      statusAfter: null,
      processResult: null,
      errorDetail: raw,
    }));
  } finally {
    clearTimeout(timeoutId);
  }
};

const useReprocessOrders = (
  options: UseReprocessOrdersOptions = {}
): UseReprocessOrdersReturn => {
  const { onComplete } = options;
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progress, setProgress] = useState<OrderReprocessProgress | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const runningRef = useRef(false);

  const startReprocess = useCallback(
    async (orderIds: string[]): Promise<void> => {
      if (runningRef.current) return;

      const uniqueIds = Array.from(
        new Set(orderIds.map((id) => id.trim()).filter(Boolean))
      );

      if (uniqueIds.length === 0) {
        setError("Selecciona al menos una orden para reprocesar.");
        return;
      }

      if (uniqueIds.length > ORDER_REPROCESS_MAX_BATCH) {
        setError(
          `Máximo ${ORDER_REPROCESS_MAX_BATCH} órdenes por lote. Reduce la selección.`
        );
        return;
      }

      runningRef.current = true;
      setError(null);
      const jobId = `local-${Date.now()}`;

      setProgressOpen(true);
      setReprocessLoading(true);
      setProgress(
        buildProgress(
          jobId,
          uniqueIds,
          [],
          uniqueIds[0] ?? null,
          "RUNNING",
          `Reprocesando lote de ${uniqueIds.length} orden(es)…`
        )
      );

      try {
        const results = await reprocessBatch(uniqueIds);

        const successCount = results.filter(
          (r) => r.success && !r.skipped
        ).length;
        const noveltyCount = results.filter(
          (r) => !r.success || r.skipped
        ).length;
        const allFailed =
          results.length > 0 &&
          successCount === 0 &&
          noveltyCount === results.length;

        const finalProgress = buildProgress(
          jobId,
          uniqueIds,
          results,
          null,
          allFailed ? "FAILED" : "COMPLETED",
          allFailed
            ? results[0]?.message || ORDER_REPROCESS_ERROR_MESSAGE
            : ORDER_REPROCESS_SUCCESS_MESSAGE
        );

        setProgress(finalProgress);
        setReprocessLoading(false);
        runningRef.current = false;
        onCompleteRef.current?.(finalProgress);
      } catch (err) {
        setReprocessLoading(false);
        runningRef.current = false;
        setError(
          translateOrderMessage(
            err instanceof Error ? err.message : "",
            ORDER_REPROCESS_ERROR_MESSAGE
          )
        );
      }
    },
    []
  );

  const closeProgress = useCallback(() => {
    if (runningRef.current) return;
    setProgressOpen(false);
    setProgress(null);
  }, []);

  return {
    reprocessLoading,
    progressOpen,
    progress,
    error,
    startReprocess,
    closeProgress,
  };
};

export default useReprocessOrders;
